import React, { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { postDemoBooking } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Calendar, Lock, ArrowRight, Sparkles } from "lucide-react";

// 7 days validity
const GATE_STORAGE_KEY = "bicenter_gate_status";
const GATE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const PREVIEW_PARAM = "preview";
const PREVIEW_TOKEN = "bicenter-2026";
const PREVIEW_STORAGE_KEY = "bicenter_preview_granted";
// Calendly link for 15-min diagnostic — user can update later
const CALENDLY_URL = "https://calendly.com/bicenter-ai/diagnostic-15min";

const REVENUE_KEYS = ["0-30k", "30k-80k", "80k-150k", "150k+"];

const readStatus = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.expires || Date.now() > parsed.expires) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeStatus = (key, value) => {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ ...value, expires: Date.now() + GATE_TTL_MS })
    );
  } catch {
    /* noop */
  }
};

export const AccessGate = ({ children }) => {
  const { t, lang } = useLanguage();
  const [status, setStatus] = useState("loading"); // loading | gate | thanks | granted

  useEffect(() => {
    // Preview bypass via ?preview=bicenter-2026
    const params = new URLSearchParams(window.location.search);
    if (params.get(PREVIEW_PARAM) === PREVIEW_TOKEN) {
      writeStatus(PREVIEW_STORAGE_KEY, { preview: true });
      setStatus("granted");
      return;
    }
    if (readStatus(PREVIEW_STORAGE_KEY)) {
      setStatus("granted");
      return;
    }
    const gate = readStatus(GATE_STORAGE_KEY);
    if (gate?.submitted) {
      setStatus("thanks");
      return;
    }
    setStatus("gate");
  }, []);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="animate-spin text-bicenter-cyan" size={32} />
      </div>
    );
  }

  if (status === "granted") return children;

  if (status === "thanks") return <ThankYouScreen lang={lang} />;

  return <GateForm t={t} lang={lang} onSuccess={() => setStatus("thanks")} />;
};

const GateForm = ({ t, lang, onSuccess }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    phone: "",
    lob_solution: "",
    revenue_range: "",
    preferred_date: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.lob_solution) {
      toast.error(lang === "fr" ? "Merci de choisir une solution" : "Please choose a solution");
      return;
    }
    if (!form.revenue_range) {
      toast.error(lang === "fr" ? "Merci d'indiquer votre CA" : "Please indicate your revenue");
      return;
    }
    setLoading(true);
    try {
      await postDemoBooking(form);
      writeStatus(GATE_STORAGE_KEY, { submitted: true, at: Date.now() });
      toast.success(t.demo.form.success);
      onSuccess();
    } catch (err) {
      toast.error(t.demo.form.error);
    } finally {
      setLoading(false);
    }
  };

  const copy = lang === "fr"
    ? {
        badge: "Accès sur invitation",
        title: "L'écosystème BICENTER AI",
        subtitle: "Accès strictement réservé aux décideurs. Réservez votre diagnostic de 15 min offert pour débloquer l'accès à nos solutions.",
        note: "Aucun accès public. Après validation par notre équipe, vous recevrez par email votre lien d'accès personnalisé et votre créneau de démo.",
      }
    : {
        badge: "Invitation only",
        title: "The BICENTER AI ecosystem",
        subtitle: "Access strictly reserved to decision-makers. Book your complimentary 15-min diagnosis to unlock our solutions.",
        note: "No public access. After validation by our team, you will receive by email your personalized access link and demo slot.",
      };

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-y-auto bg-black text-white"
      data-testid="access-gate-modal"
    >
      {/* Background aura */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-bicenter-violet/10 blur-[180px]"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-bicenter-cyan/10 blur-[160px]"></div>
      </div>

      <div className="min-h-screen flex items-center justify-center px-4 py-10 md:py-16">
        <div className="w-full max-w-6xl grid lg:grid-cols-12 gap-10 items-start">
          {/* Left column */}
          <div className="lg:col-span-5 lg:sticky lg:top-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-bicenter-cyan/30 bg-bicenter-cyan/5">
              <Lock size={12} className="text-bicenter-cyan" />
              <span className="text-[10px] uppercase tracking-[0.32em] font-mono text-bicenter-cyan">
                {copy.badge}
              </span>
            </div>
            <h1 className="mt-6 font-display text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
              {copy.title}
            </h1>
            <p className="mt-5 text-zinc-400 font-body leading-relaxed text-sm md:text-base">
              {copy.subtitle}
            </p>

            <div className="mt-8 rounded-2xl border border-white/10 bg-[#0c0c0e] p-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-bicenter-cyan to-bicenter-violet flex items-center justify-center flex-shrink-0">
                  <Sparkles size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-sm text-white font-medium">
                    {lang === "fr" ? "Diagnostic 15 min offert" : "Complimentary 15-min diagnosis"}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{copy.note}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-4 text-[10px] uppercase tracking-[0.28em] text-zinc-600 font-mono">
              <span>RGPD</span>
              <span className="w-px h-3 bg-white/10" />
              <span>AI Act</span>
              <span className="w-px h-3 bg-white/10" />
              <span>Confidentiel</span>
            </div>
          </div>

          {/* Right column - Form */}
          <div className="lg:col-span-7">
            <div className="border-gradient-primary p-[1px] rounded-3xl">
              <div className="bg-[#0c0c0e] rounded-3xl p-6 md:p-8">
                <form onSubmit={submit} className="space-y-4" data-testid="gate-form">
                  <div>
                    <h2 className="font-display text-2xl text-white font-medium">
                      {t.demo.form.title}
                    </h2>
                    <p className="text-xs text-zinc-500 mt-1">{t.demo.form.sub}</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <GateField label={t.demo.form.name} required>
                      <Input
                        required
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        data-testid="gate-input-name"
                        className="bg-white/[0.03] border-white/10 text-white h-11"
                      />
                    </GateField>
                    <GateField label={t.demo.form.email} required>
                      <Input
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        data-testid="gate-input-email"
                        className="bg-white/[0.03] border-white/10 text-white h-11"
                      />
                    </GateField>
                    <GateField label={t.demo.form.company} required>
                      <Input
                        required
                        value={form.company}
                        onChange={(e) => update("company", e.target.value)}
                        data-testid="gate-input-company"
                        className="bg-white/[0.03] border-white/10 text-white h-11"
                      />
                    </GateField>
                    <GateField label={t.demo.form.role}>
                      <Input
                        value={form.role}
                        onChange={(e) => update("role", e.target.value)}
                        data-testid="gate-input-role"
                        className="bg-white/[0.03] border-white/10 text-white h-11"
                      />
                    </GateField>
                    <GateField label={t.demo.form.phone}>
                      <Input
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        data-testid="gate-input-phone"
                        className="bg-white/[0.03] border-white/10 text-white h-11"
                      />
                    </GateField>
                    <GateField label={t.demo.form.date}>
                      <Input
                        type="date"
                        value={form.preferred_date}
                        onChange={(e) => update("preferred_date", e.target.value)}
                        data-testid="gate-input-date"
                        className="bg-white/[0.03] border-white/10 text-white h-11"
                      />
                    </GateField>
                  </div>

                  <GateField label={t.demo.form.solution} required>
                    <Select value={form.lob_solution} onValueChange={(v) => update("lob_solution", v)}>
                      <SelectTrigger
                        data-testid="gate-select-solution"
                        className="bg-white/[0.03] border-white/10 text-white h-11"
                      >
                        <SelectValue placeholder={t.demo.form.select_solution} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121214] border-white/10 text-white">
                        {t.lob.items.map((s) => (
                          <SelectItem key={s.key} value={s.key} className="focus:bg-white/10">
                            <div className="flex items-center justify-between gap-4 w-full">
                              <span>{s.name}</span>
                              <span className="text-xs text-zinc-400">· {s.day}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </GateField>

                  <GateField label={t.demo.form.revenue} required>
                    <Select value={form.revenue_range} onValueChange={(v) => update("revenue_range", v)}>
                      <SelectTrigger
                        data-testid="gate-select-revenue"
                        className="bg-white/[0.03] border-white/10 text-white h-11"
                      >
                        <SelectValue placeholder={t.demo.form.revenue_select} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121214] border-white/10 text-white">
                        {REVENUE_KEYS.map((k) => (
                          <SelectItem key={k} value={k} className="focus:bg-white/10">
                            {t.demo.form.revenue_ranges[k]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </GateField>

                  <GateField label={t.demo.form.message}>
                    <Textarea
                      rows={3}
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      data-testid="gate-input-message"
                      className="bg-white/[0.03] border-white/10 text-white"
                    />
                  </GateField>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="gate-submit-button"
                    className="w-full h-13 py-4 bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white text-base font-medium hover:shadow-[0_0_30px_rgba(0,169,255,0.45)] transition-all"
                  >
                    {loading ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 size={16} className="animate-spin" /> {t.common.loading}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        {t.demo.form.submit} <ArrowRight size={16} />
                      </span>
                    )}
                  </Button>

                  <p className="text-[10px] text-zinc-500 text-center leading-relaxed pt-1">
                    {lang === "fr"
                      ? "En soumettant ce formulaire, vous acceptez la politique de confidentialité BICENTER AI (RGPD)."
                      : "By submitting, you agree to BICENTER AI's privacy policy (GDPR)."}
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ThankYouScreen = ({ lang }) => {
  const copy = lang === "fr"
    ? {
        title: "BICENTER AI vous remercie",
        subtitle: "Votre demande a bien été reçue.",
        body:
          "Notre équipe étudie votre profil et vous reviendra sous 24h avec votre lien d'accès personnalisé et votre créneau de démo. En attendant, réservez dès maintenant votre diagnostic gratuit de 15 minutes.",
        cta: "Réserver mon diagnostic 15 min offert",
        soon: "À bientôt sur BICENTER AI.",
        badge: "Demande confirmée",
      }
    : {
        title: "BICENTER AI thanks you",
        subtitle: "Your request has been received.",
        body:
          "Our team is reviewing your profile and will get back to you within 24h with your personalized access link and demo slot. In the meantime, book your complimentary 15-minute diagnosis now.",
        cta: "Book my complimentary 15-min diagnosis",
        soon: "See you soon on BICENTER AI.",
        badge: "Request confirmed",
      };

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-y-auto bg-black text-white"
      data-testid="thank-you-screen"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-bicenter-cyan/10 blur-[200px]"></div>
      </div>

      <div className="min-h-screen flex items-center justify-center px-4 py-16">
        <div className="max-w-2xl w-full text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-bicenter-cyan/30 bg-bicenter-cyan/5 mb-8">
            <CheckCircle2 size={14} className="text-bicenter-cyan" />
            <span className="text-[10px] uppercase tracking-[0.32em] font-mono text-bicenter-cyan">
              {copy.badge}
            </span>
          </div>

          <h1 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
            {copy.title}
          </h1>
          <p className="mt-6 text-lg text-zinc-300 font-body">{copy.subtitle}</p>
          <p className="mt-4 text-zinc-400 font-body leading-relaxed max-w-xl mx-auto">
            {copy.body}
          </p>

          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="thank-you-calendly-cta"
            className="mt-10 inline-flex items-center gap-3 h-14 px-8 rounded-full bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white text-base font-medium hover:shadow-[0_0_40px_rgba(0,169,255,0.5)] transition-all"
          >
            <Calendar size={18} />
            {copy.cta}
            <ArrowRight size={18} />
          </a>

          <p className="mt-14 text-xs uppercase tracking-[0.32em] text-zinc-600 font-mono">
            {copy.soon}
          </p>
        </div>
      </div>
    </div>
  );
};

const GateField = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] uppercase tracking-[0.22em] text-zinc-400">
      {label} {required && <span className="text-bicenter-magenta">*</span>}
    </Label>
    {children}
  </div>
);
